import React, { useEffect, useState, useCallback, useRef } from "react";
import { isNullOrUndefined, extend } from "@syncfusion/ej2-base";
import {
    GridComponent,
    ColumnsDirective,
    ColumnDirective,
    Inject,
    Filter,
    Sort,
    Resize,
    Edit,
    Toolbar,
    ColumnChooser,
} from "@syncfusion/ej2-react-grids";
import {
    MDBSpinner,
    MDBIcon,
    MDBBtn,
    MDBTooltip,
    MDBRow,
    MDBCol,
    MDBModal,
    MDBModalDialog,
    MDBModalContent,
    MDBModalHeader,
    MDBModalTitle,
    MDBModalBody,
    MDBModalFooter,
} from "mdb-react-ui-kit";
import { NumericTextBoxComponent } from "@syncfusion/ej2-react-inputs";
import "./DtaGrid.css";
import { debounce } from "lodash";
import { L10n, setCulture } from "@syncfusion/ej2-base";
import enUsTrans from "./locales/en-us.json";
import zhCnTrans from "./locales/zh-cn.json";
import zhTWTrans from "./locales/zh-tw.json";
import jaTrans from "./locales/ja.json";
import { useTranslation } from "react-i18next";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css";

import "./TimelineAutosave.css";

// ====== Dialog Form Template ======
function TimelineDialogFormTemplate(props) {
    const titleRef = useRef(null);

    useEffect(() => {
        if (titleRef.current) {
            titleRef.current.focus();
        }
    }, []);

    const [val, setVal] = useState(extend({}, {}, props, true));

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setVal((prev) => ({ ...prev, [name]: value }));
    };

    const handleDurationChange = (e) => {
        setVal((prev) => ({ ...prev, DurationDays: e.value }));
    };

    let data = val;
    if (data.isAdd) {
        const keys = Object.keys(data);
        for (let key of keys) {
            if (key !== "isAdd" && isNullOrUndefined(data[key])) {
                data[key] = key === "DurationDays" ? 1 : "";
            }
        }
    }
    const isFirstRow = data.ID === 1;

    return (
        <div>
            <div className="form-row">
                <div className="form-group col-md-12">
                    <div className="e-float-input e-control-wrapper">
                        <input
                            id="ID"
                            name="ID"
                            type="text"
                            disabled={true}
                            value={data.ID || ""}
                        />
                        <span className="e-float-line"></span>
                        <label className="e-float-text e-label-top">ID</label>
                    </div>
                </div>
                <div className="form-group col-md-12">
                    <div className="e-float-input e-control-wrapper">
                        <input
                            ref={titleRef}
                            id="Title"
                            name="Title"
                            type="text"
                            value={data.Title || ""}
                            onChange={handleInputChange}
                        />
                        <span className="e-float-line"></span>
                        <label className="e-float-text e-label-top">Title</label>
                    </div>
                </div>
                <div className="form-group col-md-12">
                    <div className="e-float-input e-control-wrapper">
                        <input
                            id="CompleteUser"
                            name="CompleteUser"
                            type="text"
                            value={data.CompleteUser || ""}
                            onChange={handleInputChange}
                        />
                        <span className="e-float-line"></span>
                        <label className="e-float-text e-label-top">Complete User</label>
                    </div>
                </div>
            </div>

            <div className="form-row">
                <div className="form-group col-md-6">
                    <div className="e-float-input e-control-wrapper">
                        <input
                            id="StartDate"
                            name="StartDate"
                            format="yyyy-MM-dd"
                            type={isFirstRow ? "date" : "text"}
                            value={data.StartDate}
                            disabled={!isFirstRow}
                            onChange={handleInputChange}
                        />
                        <span className="e-float-line"></span>
                        <label className="e-float-text e-label-top">Start Date</label>
                    </div>
                </div>
                <div className="form-group col-md-6">
                    <NumericTextBoxComponent
                        id="DurationDays"
                        value={parseInt(data.DurationDays) || 1}
                        min={1}
                        format="n0"
                        max={999}
                        placeholder="Duration (Days)"
                        floatLabelType="Always"
                        change={handleDurationChange}
                    />
                </div>
            </div>
        </div>
    );
}

// ====== Main Timeline Grid Component ======
function Timeline() {
    const { t } = useTranslation();
    const query = new URLSearchParams(window.location.search);
    const id = query.get("id") || null;
    const tenantId = query.get("tenantId");

    L10n.load({
        "zh-CN": zhCnTrans,
        "zh-TW": zhTWTrans,
        en: enUsTrans,
        ja: jaTrans,
    });

    const gridInstance = useRef(null);

    useEffect(() => {
        setCulture(localStorage.getItem("i18nextLng"));
    }, [gridInstance.current]);

    const [gridData, setGridData] = useState();
    const [loading, setLoading] = useState(!!id);
    const [previewLoading, setPreviewLoading] = useState(false);

    const recalculateTimeline = useCallback((data) => {
        console.log(data);
        if (!data || data.length === 0) return data;

        const updated = data.map((item, index) => {
            let startDate = new Date(item.StartDate);
            let duration = parseInt(item.DurationDays) || 0;
            if (duration <= 0) duration = 1;

            // 计算 EndDate（不包含周末？按业务需求，这里简单 + days）
            const endDate = new Date(startDate);
            endDate.setDate(startDate.getDate() + duration);

            return {
                ...item,
                StartDate: startDate.toLocaleDateString(), // YYYY-MM-DD
                EndDate: endDate.toLocaleDateString(),
                DurationDays: duration,
            };
        });

        return updated;
    }, []);

    // 保存到后端（防抖）
    const saveToDB = useCallback(
        debounce(async (data) => {
            try {
                const response = await fetch("/api/v1/TimelineExecel", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        tenantId: tenantId,
                        model: data,
                        jobId: id,
                    }),
                });
                if (!response.ok) throw new Error(response.statusText);
                console.log("Timeline saved successfully");
            } catch (error) {
                console.error("Save failed:", error);
            }
        }, 500),
        [id, tenantId]
    );

    // 监听 gridData 变化并保存
    useEffect(() => {
        console.log(gridData);
        if (gridData) saveToDB(gridData);
    }, [gridData, saveToDB]);

    // 初始化加载
    useEffect(() => {
        async function fetchData() {
            if (!id) return;
            if (!gridInstance.current) return;
            setLoading(true);
            try {
                const resp = await fetch(
                    `/api/v1/TimelineExecel?id=${id}&tenantId=${tenantId}`
                );
                const data = await resp.json();
                if (data.result?.data) {
                    const json = JSON.parse(data.result.data);
                    const dataWithId = json.map((item, index) => ({
                        ...item, // 保留原item的所有属性
                        ID: index + 1, // 添加ID字段，从1开始计数
                    }));

                    // 初始数据也需要计算时间线
                    const recalculated = recalculateTimeline(dataWithId);
                    setGridData(recalculated);
                }
            } catch (e) {
                console.error("Fetch error:", e);
                setGridData([]);
            } finally {
                setLoading(false);
            }
        }
        fetchData();
    }, [id, tenantId, gridInstance.current]);

    // 初始空数据
    useEffect(() => {
        if (!id) {
            setGridData([]);
        }
    }, [id]);

    const getFormattedDate = (date = new Date()) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    function actionBegin(args) {
        if (args.requestType === "save") {
            //args.index = args.data.ID;
            if (
                gridInstance.current.pageSettings.currentPage !== 1 &&
                gridInstance.current.editSettings.newRowPosition === "Top"
            ) {
                args.index =
                    gridInstance.current.pageSettings.currentPage *
                    gridInstance.current.pageSettings.pageSize -
                    gridInstance.current.pageSettings.pageSize;
            } else if (
                gridInstance.current.editSettings.newRowPosition === "Bottom"
            ) {
                args.index =
                    gridInstance.current.pageSettings.currentPage *
                    gridInstance.current.pageSettings.pageSize -
                    1;
            }
        } else if (args.requestType === "add") {
            // ✅ 使用正确的字段名初始化新行
            if (gridData.length === 0) {
                args.rowData = {
                    ID: gridData.length + 1,
                    Title: "",
                    CompleteUser: "",
                    DurationDays: 1,
                    StartDate: getFormattedDate(),
                };
            } else {
                args.rowData = {
                    ID: gridData.length + 1,
                    Title: "",
                    CompleteUser: "",
                    DurationDays: 1,
                    StartDate: gridData[gridData.length - 1].EndDate,
                };
            }
        }
    }

    const actionComplete = (args) => {
        if (args.requestType === "delete") {
            // const recalculated = recalculateTimeline(gridInstance.current.dataSource);
            // setGridData(recalculated);
            setTimeout(() => {
                const recalculated = recalculateTimeline(gridInstance.current.dataSource);
                setGridData(recalculated);
            }, 500);
        } else if (args.requestType === "save") {
            setTimeout(() => {
                const recalculated = recalculateTimeline(gridInstance.current.dataSource);
                setGridData(recalculated);
            }, 500);
        }
    };

    const toolbarClick = (args) => {
        if (args.item.text === "Add" && gridData && gridData.length >= 50) {
            alert("Maximum 50 timeline items allowed");
            args.cancel = true;
        }
        if (args.item.text === t("Preview")) {
            if (previewLoading) {
                args.cancel = true;
                return;
            }
            preview();
        }
    };

    const editSettings = {
        allowEditing: true,
        allowAdding: true,
        showAddNewRow: false,
        allowDeleting: true,
        newRowPosition: "Bottom",
        mode: "Dialog",
        template: dialogTemplate,
    };

    function dialogTemplate(props) {
        return <TimelineDialogFormTemplate {...props} />;
    }
    const toolbarOptions = ["Add", "Edit", "Delete", t("Preview")];
    const [showModal, setShowModal] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);

    const [previewUrl, setPreviewUrl] = useState(null);
    const preview = async () => {
        try {
            setPreviewLoading(true);

            // 等待当前防抖保存完成（如果存在挂起的保存调用）
            let pendingSavePromise = null;
            if (saveToDB && typeof saveToDB.flush === "function") {
                pendingSavePromise = saveToDB.flush();
            }
            if (pendingSavePromise && typeof pendingSavePromise.then === "function") {
                await pendingSavePromise;
            }

            // 新一轮预览前重置图片加载状态
            setImageLoaded(false);
            setShowModal(true);
            setPreviewUrl(null);

            const response = await fetch(
                `/api/v1/Preview?id=${id}&tenantId=${tenantId}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`失败：${response.statusText}`);
            }

            const result = await response.json();
            setPreviewUrl(result.result.url);
        } catch (error) {
            console.error("Preview failed:", error);
        } finally {
            setPreviewLoading(false);
        }
    };

    return (
        <div className="control-pane">
            {/* 自动保存提示 */}
            <div className="autosave-indicator">
                {t("Autosave")}
            </div>
            <div className="control-section vh-100">
                {(loading || previewLoading) && (
                    <div
                        className="diagram-loading-indicator"
                        style={{
                            position: "absolute",
                            placeSelf: "anchor-center",
                            textAlign: "center",
                            top: 10,
                            right: 10,
                            zIndex: 10,
                        }}
                    >
                        <MDBSpinner grow color="primary">
                            <span className="visually-hidden">Loading...</span>
                        </MDBSpinner>
                        <MDBSpinner grow className="ms-2" color="dark">
                            <span className="visually-hidden">Loading...</span>
                        </MDBSpinner>
                        <MDBSpinner grow className="mx-2" color="secondary">
                            <span className="visually-hidden">Loading...</span>
                        </MDBSpinner>
                    </div>
                )}
                {showModal && previewUrl && (
                    <Lightbox
                        key={showModal}
                        mainSrc={previewUrl}
                        onCloseRequest={() => {
                            setShowModal(false);
                            setPreviewUrl(null);
                            setImageLoaded(false);
                        }}
                        clickOutsideToClose={false}
                        reactModalStyle={{
                            overlay: {
                                zIndex: 1400,
                            },
                        }}
                        onImageLoad={() => {
                            setImageLoaded(true);
                        }}
                        onAfterOpen={() => { }}
                        onImageLoadError={() => { }}
                        imageLoadErrorMessage={() => { }}
                        loader={
                            <div style={{ display: imageLoaded ? "none" : "block" }}>
                                ...
                            </div>
                        }
                        imageTitle={null}
                        imageCaption={null}
                    />
                )}
                <GridComponent
                    locale={localStorage.getItem("i18nextLng")}
                    dataSource={gridData}
                    toolbar={toolbarOptions}
                    editSettings={editSettings}
                    ref={(grid) => (gridInstance.current = grid)}
                    actionBegin={actionBegin}
                    actionComplete={actionComplete}
                    toolbarClick={toolbarClick}
                    clipMode="EllipsisWithTooltip"
                    rowHeight={50}
                    allowTextWrap={true}
                >
                    <ColumnsDirective>
                        <ColumnDirective
                            field="ID"
                            headerText="ID"
                            width="60"
                            textAlign="Center"
                            allowEditing={false}
                            isPrimaryKey={true}
                            type="number"
                        />
                        <ColumnDirective
                            field="Title"
                            headerText="Title"
                            width="200"
                            validationRules={{ required: true }}
                        />
                        <ColumnDirective
                            field="CompleteUser"
                            headerText="Complete User"
                            width="150"
                        />
                        <ColumnDirective
                            field="DurationDays"
                            headerText="Duration (Days)"
                            width="120"
                            editType="dropdownedit"
                            type="number"
                        />
                        <ColumnDirective
                            field="StartDate"
                            headerText="Start Date"
                            width="120"
                            allowEditing={true}
                        />
                        <ColumnDirective
                            field="EndDate"
                            headerText="End Date"
                            width="120"
                            allowEditing={false}
                        />
                    </ColumnsDirective>
                    <Inject
                        services={[Filter, Sort, Resize, Edit, Toolbar, ColumnChooser]}
                    />
                </GridComponent>
            </div>
        </div>
    );
}

export default Timeline;

